"use client";

import { useEffect, useState } from "react";

import { rememberAuthHint } from "@/lib/nav-auth-hint";
import type { SubscriptionData } from "@/types/subscriptions";

interface PublicNavState {
  user: { id: string; email: string | null } | null;
  subscriptions: SubscriptionData[];
}

const LOGGED_OUT_STATE: PublicNavState = { user: null, subscriptions: [] };

let cache: PublicNavState | null = null;
let generation = 0;
let inflight: { generation: number; promise: Promise<PublicNavState> } | null = null;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

export function loadPublicNavState(): Promise<PublicNavState> {
  if (cache) return Promise.resolve(cache);
  if (!inflight || inflight.generation !== generation) {
    const requestGeneration = generation;
    const promise = fetch("/api/auth/nav-state", { credentials: "same-origin" })
      .then(async (res): Promise<PublicNavState> => {
        if (!res.ok) throw new Error(`nav-state ${res.status}`);
        const data = (await res.json()) as PublicNavState;
        if (requestGeneration === generation) {
          cache = data;
          rememberAuthHint(Boolean(data.user));
          notify();
          return data;
        }
        return cache ?? LOGGED_OUT_STATE;
      })
      .catch(() => {
        if (requestGeneration !== generation) {
          return (
            cache ??
            (inflight?.generation === generation
              ? inflight.promise
              : LOGGED_OUT_STATE)
          );
        }
        notify();
        return LOGGED_OUT_STATE;
      })
      .finally(() => {
        if (inflight?.generation === requestGeneration) {
          inflight = null;
        }
      });
    inflight = { generation: requestGeneration, promise };
  }
  return inflight.promise;
}

export interface PublicAuth {
  user: { id: string; email: string | null } | null;
  subscriptions: SubscriptionData[];
  isAuthenticated: boolean;
  isLoading: boolean;
  signOut: () => Promise<boolean>;
  refresh: () => Promise<void>;
}

export function usePublicAuth(): PublicAuth {
  const [state, setState] = useState<PublicNavState | null>(null);

  useEffect(() => {
    let cancelled = false;
    const update = () => {
      if (cache) setState(cache);
    };
    listeners.add(update);
    if (!cache) {
      void loadPublicNavState().then((result) => {
        if (!cancelled) setState(result);
      });
    } else {
      setState(cache);
    }
    return () => {
      listeners.delete(update);
      cancelled = true;
    };
  }, []);

  return {
    user: state?.user ?? null,
    subscriptions: state?.subscriptions ?? [],
    isAuthenticated: Boolean(state?.user),
    isLoading: state === null,
    signOut: async () => {
      const res = await fetch("/api/auth/nav-state", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ action: "signOut" }),
      }).catch(() => null);
      if (!res?.ok) return false;
      generation++;
      cache = LOGGED_OUT_STATE;
      rememberAuthHint(false);
      notify();
      return true;
    },
    refresh: async () => {
      await refreshPublicNavState();
    },
  };
}

export function refreshPublicNavState(): Promise<PublicNavState> {
  cache = null;
  generation++;
  return loadPublicNavState();
}


export function seedPublicNavState(
  user: { id: string; email?: string | null } | null,
  subscriptions: SubscriptionData[],
): void {
  generation++;
  cache = user
    ? { user: { id: user.id, email: user.email ?? null }, subscriptions }
    : LOGGED_OUT_STATE;
  rememberAuthHint(Boolean(cache.user));
  notify();
}

export function invalidatePublicNavState(): void {
  generation++;
  cache = null;
  rememberAuthHint(false);
}
